/* eslint-disable react/no-unknown-property */
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { forwardRef, useRef, useMemo, useLayoutEffect, useEffect, useState } from "react";
import { Color } from "three";

const hexToNormalizedRGB = (hex) => {
  hex = hex.replace("#", "");
  return [
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255,
  ];
};

const vertexShader = `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
  vPosition = position;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = `
varying vec2 vUv;
varying vec3 vPosition;

uniform float uTime;
uniform vec3  uColor;
uniform float uSpeed;
uniform float uScale;
uniform float uRotation;
uniform float uNoiseIntensity;

const float e = 2.71828182845904523536;

float noise(vec2 texCoord) {
  float G = e;
  vec2  r = (G * sin(G * texCoord));
  return fract(r.x * r.y * (1.0 + texCoord.x));
}

vec2 rotateUvs(vec2 uv, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  mat2  rot = mat2(c, -s, s, c);
  return rot * uv;
}

void main() {
  float rnd        = noise(gl_FragCoord.xy);
  vec2  uv         = rotateUvs(vUv * uScale, uRotation);
  vec2  tex        = uv * uScale;
  float tOffset    = uSpeed * uTime;

  tex.y += 0.03 * sin(8.0 * tex.x - tOffset);

  float pattern = 0.6 +
                  0.4 * sin(5.0 * (tex.x + tex.y +
                                   cos(3.0 * tex.x + 5.0 * tex.y) +
                                   0.02 * tOffset) +
                           sin(20.0 * (tex.x + tex.y - 0.1 * tOffset)));

  vec4 col = vec4(uColor, 1.0) * vec4(pattern) - rnd / 15.0 * uNoiseIntensity;
  col.a = 1.0;
  gl_FragColor = col;
}
`;

const SilkPlane = forwardRef(function SilkPlane({ uniforms, speed, scale, color, noiseIntensity, rotation, isMobile }, ref) {
  const { viewport, invalidate } = useThree();

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.scale.set(viewport.width, viewport.height, 1);
    }
  }, [ref, viewport]);

  // Update uniforms when props change
  useEffect(() => {
    if (ref.current && ref.current.material && ref.current.material.uniforms) {
      ref.current.material.uniforms.uSpeed.value = speed;
      ref.current.material.uniforms.uScale.value = scale;
      ref.current.material.uniforms.uNoiseIntensity.value = noiseIntensity;
      ref.current.material.uniforms.uColor.value = new Color(...hexToNormalizedRGB(color));
      ref.current.material.uniforms.uRotation.value = rotation;
      invalidate(); // Trigger re-render when props change
    }
  }, [speed, scale, color, noiseIntensity, rotation, ref, invalidate]);

  useFrame((_, delta) => {
    if (ref.current && ref.current.material && ref.current.material.uniforms) {
      // Reduce animation frequency on mobile for better performance
      const frameSkip = isMobile ? 3 : 1;
      if (isMobile) {
        // On mobile, only update every 3rd frame and invalidate manually
        ref.current.material.uniforms.uTime.value += 0.1 * delta * frameSkip;
        if (Math.floor(ref.current.material.uniforms.uTime.value * 10) % frameSkip === 0) {
          invalidate();
        }
      } else {
        // On desktop, smooth animation
        ref.current.material.uniforms.uTime.value += 0.1 * delta;
      }
    }
  });

  return (
    <mesh ref={ref}>
      <planeGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
      />
    </mesh>
  );
});
SilkPlane.displayName = "SilkPlane";

const Silk = ({
  speed = 5,
  scale = 1,
  color = "#7B7481",
  noiseIntensity = 1.5,
  rotation = 0,
}) => {
  const meshRef = useRef();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices for performance optimization
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const uniforms = useMemo(
    () => ({
      uSpeed: { value: speed },
      uScale: { value: scale },
      uNoiseIntensity: { value: noiseIntensity },
      uColor: { value: new Color(...hexToNormalizedRGB(color)) },
      uRotation: { value: rotation },
      uTime: { value: 0 },
    }),
    [speed, scale, noiseIntensity, color, rotation]
  );

  // Performance optimizations for mobile
  const canvasProps = isMobile
    ? {
        dpr: [0.5, 1], // Lower DPR on mobile
        frameloop: "demand", // Only render when needed
        performance: { min: 0.2, max: 1, debounce: 200 } // Throttle performance
      }
    : {
        dpr: [1, 2], // Full quality on desktop
        frameloop: "always"
      };

  return (
    <Canvas {...canvasProps} suppressHydrationWarning>
      <SilkPlane
        ref={meshRef}
        uniforms={uniforms}
        speed={speed}
        scale={scale}
        color={color}
        noiseIntensity={noiseIntensity}
        rotation={rotation}
        isMobile={isMobile}
      />
    </Canvas>
  );
};

export default Silk;
